import React from "react";
import { useMutation } from "@apollo/client";
import { ApolloError, gql } from "@apollo/client";
import { Form, Formik, FormikProps } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { Button } from "src/components/button/button";
import { Input } from "src/components/input/input";
import { SectionTitle } from "src/components/typography/typography";
import { CreateSchoolFieldsInput, CreateSchoolInput, CreateSchoolPayload } from "src/generated/types";
import { parseError } from "src/utils/parse_error";

import { AdminLayout } from "./admin_layout";

/*
 * GraphQL
 */

export const CREATE_SCHOOL_MUTATION = gql`
    mutation CreateSchool($input: CreateSchoolInput!) {
        createSchool(input: $input) {
            school {
                id
                accountNumber
                address
                name
            }
        }
    }
`;

/*
 * Types
 */

interface ApolloMutationVars {
    input: CreateSchoolInput;
}

interface ApolloMutationData {
    createSchool: CreateSchoolPayload;
}

interface FormValues {
    accountNumber: string;
    address: string;
    name: string;
}

/*
 * Helpers
 */

/*
 * The ACSI account number is optional: a blank entry is left off the new school entirely rather
 * than stored as an empty string, so a school "without an account number" looks the same whether it
 * was created before or after INC-25784.
 */
export const toCreateSchoolFields = (values: FormValues): CreateSchoolFieldsInput => {
    const accountNumber = values.accountNumber.trim();

    return {
        address: values.address,
        name: values.name,
        ...(accountNumber ? { accountNumber } : {}),
    };
};

/*
 * Main
 */

export const CreateSchoolPage = (): React.ReactElement => {
    const [createSchool, { loading }] = useMutation<ApolloMutationData, ApolloMutationVars>(CREATE_SCHOOL_MUTATION);

    const initialValues: FormValues = {
        accountNumber: "",
        address: "",
        name: "",
    };

    return (
        <AdminLayout>
            <Formik
                initialValues={initialValues}
                onSubmit={(values, { resetForm, setSubmitting }): void => {
                    createSchool({
                        variables: {
                            input: {
                                fields: toCreateSchoolFields(values),
                            },
                        },
                    })
                        .then(() => {
                            toast.success("School created successfully");
                        })
                        .catch((e: ApolloError) => {
                            toast.error(parseError(e));
                        });

                    setSubmitting(false);
                    resetForm();
                }}
                validationSchema={Yup.object().shape({
                    accountNumber: Yup.string(),
                    address: Yup.string().required("Required"),
                    name: Yup.string().required("Required"),
                })}
            >
                {(formik: FormikProps<FormValues>) => (
                    <Form>
                        <SectionTitle>Create a New School</SectionTitle>
                        <Input autoComplete="off" id="name" label="Name" name="name" placeholder="Organization Name" />
                        <Input
                            autoComplete="off"
                            id="address"
                            label="Address"
                            name="address"
                            placeholder="Organization Address"
                        />
                        <Input
                            autoComplete="off"
                            id="accountNumber"
                            label="Account Number"
                            name="accountNumber"
                            placeholder="ACSI Account Number (optional)"
                        />
                        <Button disabled={formik.isSubmitting || !formik.isValid || loading} type="submit">
                            Submit
                        </Button>
                    </Form>
                )}
            </Formik>
        </AdminLayout>
    );
};
